// src/components/well3d/Well3DSurface.jsx
//
// Cesium viewer that mounts as a sibling of MapView when the Well3D
// product is active. Scopes to the user's selected PROJECT AOI (smaller,
// more focused). Wells are still filtered by the operating AOI for
// spatial entitlement.

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import { fetchWithAuth } from '../../api/fetchWithAuth';
import { TileSessionManager, tileSessionOptions } from './tileSession';
import { useAoiStore } from '../../stores/useAoiStore';
import { useOperatingAoiContext } from '../../stores/useOperatingAoiContext';
import { useProjectStore } from '../../stores/useProjectStore';
import { useImageryStackStore } from '../../stores/useImageryStackStore';
import { useWell3DStore } from '../../stores/useWell3DStore';

/* =========================================================================
   Reused geometry helpers — same math as WellDirectionalSection so the
   3D view matches the 2D panel.
   ========================================================================= */

const FORMATION_COLORS = {
  'Wolfcamp A':   '#378ADD',
  'Wolfcamp B':   '#1D9E75',
  'Wolfcamp':     '#378ADD',
  'Bone Spring':  '#EF9F27',
  'Spraberry':    '#D85A30',
  'Delaware Mtn': '#7F77DD',
  'Eagle Ford':   '#D4537E',
  'Haynesville':  '#639922',
  'Barnett':      '#BA7517',
  'Austin Chalk': '#5DCAA5',
};

function getFormationColor(name) {
  if (!name) return '#888780';
  for (const [key, color] of Object.entries(FORMATION_COLORS)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#888780';
}

const TO_RAD = Math.PI / 180;
const FT_PER_DEG_LAT = 364000;

function seededRandom(api) {
  let h = 0;
  for (let i = 0; i < (api || '').length; i++)
    h = Math.imul(31, h) + api.charCodeAt(i) | 0;
  return Math.abs(h % 1000) / 1000;
}

function buildVerticalPath(surfLon, surfLat, tvdFt, api) {
  const seed = seededRandom(api);
  const inclMax = 2 + seed * 3;
  const azimuth = seed * 360;
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  const steps = 30;
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const frac = i / steps;
    const tvd = tvdFt * frac;
    const incl = inclMax * Math.sin(frac * Math.PI * 0.5) * TO_RAD;
    const azi = azimuth * TO_RAD;
    const horizFt = tvd * Math.tan(incl);
    pts.push([
      surfLon + (horizFt * Math.sin(azi)) / ftPerDegLon,
      surfLat + (horizFt * Math.cos(azi)) / FT_PER_DEG_LAT,
      -(tvd * 0.3048),
    ]);
  }
  return pts;
}

function buildJCurve(surfLon, surfLat, heelLon, heelLat, toeLon, toeLat, tvdFt) {
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  const dLon = (toeLon - heelLon) * ftPerDegLon;
  const dLat = (toeLat - heelLat) * FT_PER_DEG_LAT;
  const azDeg = (Math.atan2(dLon, dLat) * (180 / Math.PI) + 360) % 360;
  const latFt = Math.sqrt(dLon * dLon + dLat * dLat);
  const bur = 6, kickoff = tvdFt * 0.6, buildFt = (90 / bur) * 100;
  const totalMD = kickoff + buildFt + latFt;
  const survey = [];
  for (let i = 0; i <= 40; i++) {
    const md = totalMD * (i / 40);
    let incl = 0;
    if (md > kickoff && md < kickoff + buildFt) incl = Math.min(90, ((md - kickoff) / 100) * bur);
    else if (md >= kickoff + buildFt) incl = 90;
    survey.push({ md, incl, azi: azDeg });
  }
  const pts = [{ x: 0, y: 0, z: 0 }];
  for (let i = 1; i < survey.length; i++) {
    const p = survey[i - 1], c = survey[i], dmd = c.md - p.md;
    const i1 = p.incl * TO_RAD, i2 = c.incl * TO_RAD, a1 = p.azi * TO_RAD, a2 = c.azi * TO_RAD;
    const arg = Math.cos(i2 - i1) - Math.sin(i1) * Math.sin(i2) * (1 - Math.cos(a2 - a1));
    const dl = Math.acos(Math.max(-1, Math.min(1, arg)));
    const rf = dl < 1e-4 ? 1 : (2 / dl) * Math.tan(dl / 2);
    const last = pts[pts.length - 1];
    pts.push({
      x: last.x + (dmd / 2) * (Math.sin(i1) * Math.sin(a1) + Math.sin(i2) * Math.sin(a2)) * rf,
      y: last.y + (dmd / 2) * (Math.sin(i1) * Math.cos(a1) + Math.sin(i2) * Math.cos(a2)) * rf,
      z: last.z + (dmd / 2) * (Math.cos(i1) + Math.cos(i2)) * rf,
    });
  }
  return pts.map(p => [
    surfLon + p.x / ftPerDegLon,
    surfLat + p.y / FT_PER_DEG_LAT,
    -(p.z * 0.3048),
  ]);
}

function wellToCesiumPath(w) {
  const { surfLon, surfLat, heelLon, heelLat, toeLon, toeLat,
          tvdFt, formation, api, wellType } = w;
  if (!surfLon || !surfLat || !tvdFt || tvdFt <= 0) return null;
  if (!Number.isFinite(surfLon) || !Number.isFinite(surfLat) || !Number.isFinite(tvdFt)) return null;

  const heelToeMissing =
    heelLon == null || heelLat == null || toeLon == null || toeLat == null;
  const heelToeCoincident = !heelToeMissing &&
    Math.abs(heelLon - toeLon) < 1e-7 &&
    Math.abs(heelLat - toeLat) < 1e-7;
  const isVertical =
    wellType === 'vertical' || heelToeMissing || heelToeCoincident;

  const coords = isVertical
    ? buildVerticalPath(surfLon, surfLat, tvdFt, api || w.id || 'unknown')
    : buildJCurve(surfLon, surfLat, heelLon, heelLat, toeLon, toeLat, tvdFt);

  const positions = [];
  for (const c of coords) {
    if (!c || c.length !== 3) continue;
    const [lon, lat, z] = c;
    if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(z)) continue;
    if (Math.abs(lat) > 89.99) continue;
    const pt = Cesium.Cartesian3.fromDegrees(lon, lat, z);
    if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y) || !Number.isFinite(pt.z)) continue;
    const last = positions[positions.length - 1];
    if (last) {
      const dx = pt.x - last.x, dy = pt.y - last.y, dz = pt.z - last.z;
      if (dx*dx + dy*dy + dz*dz < 0.25) continue;
    }
    positions.push(pt);
  }
  if (positions.length < 2) return null;

  return {
    positions,
    color: Cesium.Color.fromCssColorString(getFormationColor(formation)),
    meta: { api, formation, tvdFt, wellType, surfLon, surfLat },
  };
}

async function fetchInPool(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const i = cursor++;
      try { results[i] = await fn(items[i], i); }
      catch (e) { results[i] = { __error: e }; }
    }
  }
  await Promise.all(Array.from({ length: limit }, worker));
  return results;
}

/* =========================================================================
   bbox from a project AOI — handles Polygon, MultiPolygon, GeometryCollection.
   Project AOIs store geometry in aoi.geom as GeoJSON.
   ========================================================================= */
function bboxFromProjectAoi(aoi) {
  if (!aoi) return null;
  const geom = aoi.geom ?? aoi.geometry;
  if (!geom?.coordinates) return null;

  const coords = [];
  const walk = (c) =>
    typeof c[0] === 'number' ? coords.push(c) : c.forEach(walk);
  walk(geom.coordinates);

  if (!coords.length) return null;
  const lons = coords.map(p => p[0]);
  const lats = coords.map(p => p[1]);
  return {
    west:  Math.min(...lons),
    south: Math.min(...lats),
    east:  Math.max(...lons),
    north: Math.max(...lats),
  };
}

/* =========================================================================
   Well3DSurface
   ========================================================================= */

const Well3DSurface = forwardRef(function Well3DSurface(_props, ref) {
  const containerRef = useRef(null);
  const viewerRef    = useRef(null);
  const sessionRef   = useRef(null);

  /* ---- Project AOI: drives bbox and dataset selection ---- */
  const aois          = useAoiStore(s => s.aois);
  const selectedAoiId = useAoiStore(s => s.selectedAoiId);
  const activeAoiId   = useAoiStore(s => s.activeAoiId);
  const coverageByAoi = useAoiStore(s => s.imageryCoverageByAoi);
  const loadCoverage  = useAoiStore(s => s.loadImageryCoverage);

  // Prefer AOI pinned as a map layer; fall back to AOI being edited
  const projectAoiId  = selectedAoiId || activeAoiId;
  const projectAoi    = aois.find(a => a.id === projectAoiId);

  /* ---- Operating AOI: still used for wells query (spatial entitlement) ---- */
  const activeOperatingAoiId = useOperatingAoiContext(s => s.activeOperatingAoiId);

  /* ---- Project: used by /tile/session ---- */
  const activeProjectId = useProjectStore(s => s.activeProjectId);
  const projects        = useProjectStore(s => s.projects);
  const project         = projects.find(
    p => p.slug === activeProjectId || p.id === activeProjectId
  );

  /* ---- Trigger coverage lookup once we know which AOI is in focus ---- */
  useEffect(() => {
    if (projectAoiId) loadCoverage(projectAoiId);
  }, [projectAoiId, loadCoverage]);

  const bbox = bboxFromProjectAoi(projectAoi);

  /* ---- Pick the dataset whose imagery actually covers this AOI ----
     The coverage endpoint returns dataset_id and dataset_name but not
     dataset_key (the dotted key the tile server needs). Resolve in order:
       1. Find the dataset_id in catalog.series and use its dataset_key
       2. If catalog isn't loaded, construct the key from dataset_name
          ("Permian Midland 2025_12" → "imagery.permian.midland.2025_12") */
  const coverage = coverageByAoi[projectAoiId];
  const catalog  = useImageryStackStore(s => s.catalog);

  const datasetKey = (() => {
    if (!coverage?.datasets?.length) return null;
    const selected = coverage.datasets.find(
      d => d.dataset_id === coverage.selectedDatasetId
    );
    if (!selected) return null;

    // Catalog lookup by dataset_id
    for (const series of catalog?.series ?? []) {
      for (const acq of series.acquisitions ?? []) {
        if (acq.dataset_id === selected.dataset_id) return acq.dataset_key;
      }
    }

    // Fallback: construct from dataset_name
    if (selected.dataset_name) {
      return 'imagery.' + selected.dataset_name.toLowerCase().replace(/\s+/g, '.');
    }
    return null;
  })();

  /* ---- Wells store ---- */
  const setWells        = useWell3DStore(s => s.setWells);
  const setSurfaceWells = useWell3DStore(s => s.setSurfaceWells);
  const setLoading      = useWell3DStore(s => s.setLoading);
  const setLoadMsg      = useWell3DStore(s => s.setLoadMsg);
  const setError        = useWell3DStore(s => s.setError);
  const setSelected     = useWell3DStore(s => s.setSelected);
  const setCamera       = useWell3DStore(s => s.setCamera);
  const wells           = useWell3DStore(s => s.wells);
  const surfaceWells    = useWell3DStore(s => s.surfaceWells);

  /* ---- Viewer init (once we have all the inputs) ---- */
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;
    if (!bbox || !datasetKey || !projectAoiId) return;

    let cancelled = false;

    (async () => {
      try {
        Cesium.Ion.defaultAccessToken = '';

        setLoadMsg('Authenticating tile session…');
      const session = new TileSessionManager(tileSessionOptions({
        datasetKey,
        operatingAoi: activeOperatingAoiId,
        projectId:    project?.id,
        onError: (e) => setError(`Tile session: ${e.message}`),
      }));
      await session.start();
      if (cancelled) return;
      sessionRef.current = session;

      const coverageRect = Cesium.Rectangle.fromDegrees(
        bbox.west, bbox.south, bbox.east, bbox.north
      );

      const viewer = new Cesium.Viewer(containerRef.current, {
        baseLayer: false,
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        animation: false,
        timeline: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
        terrainProvider: new Cesium.EllipsoidTerrainProvider(),
        creditContainer: document.createElement('div'),
      });

      const imageryProvider = new Cesium.UrlTemplateImageryProvider({
        url: '{z}/{x}/{y}',
        minimumLevel: 12,
        maximumLevel: 22,
        rectangle: coverageRect,
        tilingScheme: new Cesium.WebMercatorTilingScheme(),
      });

      // Compute the tile-level intersection check. Cesium will *still* ask
      // for tiles outside the rectangle if it thinks they're visible — we
      // reject those at the requestImage layer so no imagery gets painted
      // into them. The black mask layer underneath shows through.
      const tilingScheme = imageryProvider.tilingScheme;
      const rectIntersectsTile = (level, x, y) => {
        const tileRect = tilingScheme.tileXYToRectangle(x, y, level);
        return Cesium.Rectangle.intersection(tileRect, coverageRect) !== undefined;
      };

      imageryProvider.requestImage = (x, y, level, request) => {
        // Reject tiles outside the AOI footprint so Cesium leaves them
        // untextured (showing the black mask below). This is the real
        // streak suppressor.
        if (!rectIntersectsTile(level, x, y)) {
          return Promise.reject(new Cesium.RequestErrorEvent());
        }
        const url = sessionRef.current?.tileUrl(level, x, y);
        if (!url) return Promise.reject(new Error('No tile session'));
        return Cesium.Resource.fetchImage({ url, request });
      };
      viewer.imageryLayers.removeAll();

      // Base layer: a single black raster that covers the entire globe.
      // Anywhere the AOI imagery doesn't cover, this shows through — so
      // instead of streaks from edge-pixel stretching, you see black.
      const BLACK_PNG_DATA_URL =
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';
      try {
        // Cesium 1.115 requires tileWidth/tileHeight on SingleTileImageryProvider.
        // The PNG is 1×1 so dimensions are literally 1, 1.
        viewer.imageryLayers.addImageryProvider(
          new Cesium.SingleTileImageryProvider({
            url: BLACK_PNG_DATA_URL,
            rectangle: Cesium.Rectangle.MAX_VALUE,
            tileWidth: 1,
            tileHeight: 1,
          })
        );
      } catch (e) {
        console.warn('[well3d] could not add base mask layer:', e);
      }

      // AOI imagery on top of the black mask.
      // Construct an ImageryLayer explicitly so we can pass rectangle as a
      // construction option — layer.rectangle is read-only after construction
      // in Cesium 1.115.
      try {
        const imageryLayer = new Cesium.ImageryLayer(imageryProvider, {
          rectangle: coverageRect,
          alpha: 1.0,
          // Imagery looks dim against the dark void. Bump brightness slightly
          // and lift the gamma curve so midtones aren't crushed.
          brightness: 1.3,
          contrast:   1.05,
          gamma:      0.85,
        });
        viewer.imageryLayers.add(imageryLayer);
      } catch (e) {
        console.warn('[well3d] could not add imagery layer:', e);
        // Fallback: add the provider directly, no rectangle constraint.
        viewer.imageryLayers.addImageryProvider(imageryProvider);
      }

      viewer.scene.globe.translucency.enabled = true;
      viewer.scene.globe.translucency.frontFaceAlpha = 1.0;
      viewer.scene.globe.depthTestAgainstTerrain = false;
      viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#080c10');

      // Replace Cesium's default sun-based lighting with a fixed directional
      // light. Default sun lighting makes the imagery go very dark when the
      // camera looks at certain angles relative to the sun's position; for
      // a presentation/demo context we want consistent brightness everywhere.
      try {
        viewer.scene.light = new Cesium.DirectionalLight({
          direction: new Cesium.Cartesian3(0.5, 0.5, -0.7),
          intensity: 2.5,
        });
      } catch (e) {
        console.warn('[well3d] could not set scene light:', e);
      }

      // ───── Clip the globe surface to the AOI rectangle ─────
      // This is the streak suppressor. `layer.rectangle` and tile-request
      // rejection don't stop Cesium from stretching tile textures across
      // the entire globe surface at the GPU sampler level. The only fix
      // that actually works is to clip the globe geometry itself so the
      // surface only exists inside the AOI.
      //
      // Four ECEF planes (north, south, east, west) bound the AOI volume.
      // Each plane is a tangent to the ellipsoid at the AOI edge with its
      // normal pointing inward. Globe geometry on the outside of any plane
      // is culled.
      try {
        const ellipsoid = viewer.scene.globe.ellipsoid;
        const center    = Cesium.Cartesian3.fromDegrees(
          (bbox.west + bbox.east) / 2,
          (bbox.south + bbox.north) / 2,
        );

        // Four corner points
        const nw = Cesium.Cartesian3.fromDegrees(bbox.west, bbox.north);
        const ne = Cesium.Cartesian3.fromDegrees(bbox.east, bbox.north);
        const sw = Cesium.Cartesian3.fromDegrees(bbox.west, bbox.south);
        const se = Cesium.Cartesian3.fromDegrees(bbox.east, bbox.south);

        // Helper: build a plane from three points where the inward-facing
        // normal aims toward `inside`.
        const planeFromEdge = (a, b, inside) => {
          const ab = Cesium.Cartesian3.subtract(b, a, new Cesium.Cartesian3());
          // Use the ellipsoid normal at `a` as the up vector so the plane
          // is vertical (a tangent wall).
          const up = ellipsoid.geodeticSurfaceNormal(a, new Cesium.Cartesian3());
          let normal = Cesium.Cartesian3.cross(ab, up, new Cesium.Cartesian3());
          Cesium.Cartesian3.normalize(normal, normal);
          // Flip if needed so the normal points inward toward `inside`
          const ai = Cesium.Cartesian3.subtract(inside, a, new Cesium.Cartesian3());
          if (Cesium.Cartesian3.dot(normal, ai) < 0) {
            Cesium.Cartesian3.negate(normal, normal);
          }
          return Cesium.Plane.fromPointNormal(a, normal);
        };

        const planes = [
          planeFromEdge(nw, ne, center),  // north edge, normal points south (inward)
          planeFromEdge(se, sw, center),  // south edge, normal points north
          planeFromEdge(ne, se, center),  // east edge,  normal points west
          planeFromEdge(sw, nw, center),  // west edge,  normal points east
        ];

        // Convert each Plane to a ClippingPlane (Cesium's clipping-plane type).
        // Globe.clippingPlanes wants Cesium.ClippingPlane instances, not Cesium.Plane.
        const clippingPlanes = planes.map(p =>
          new Cesium.ClippingPlane(p.normal, p.distance)
        );

        viewer.scene.globe.clippingPlanes = new Cesium.ClippingPlaneCollection({
          planes: clippingPlanes,
          unionClippingRegions: false,  // INTERSECT — globe survives only where ALL planes say "inside"
          edgeWidth: 0,
        });
      } catch (e) {
        console.warn('[well3d] globe clipping setup failed:', e);
      }

      // ───── Cutaway treatment ─────
      // Each piece is wrapped individually so one failure doesn't kill the
      // whole viewer init. globe.baseColor in particular varies by version.
      try {
        if ('baseColor' in viewer.scene.globe) {
          viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString('#080c10');
        }
      } catch (e) {
        console.warn('[well3d] globe.baseColor failed:', e);
      }

      try {
        const { west, south, east, north } = bbox;

        // Surface frame around the AOI
        viewer.entities.add({
          id: 'aoi-frame',
          polyline: {
            positions: Cesium.Cartesian3.fromDegreesArrayHeights([
              west, south, 5,
              east, south, 5,
              east, north, 5,
              west, north, 5,
              west, south, 5,
            ]),
            width: 2,
            material: Cesium.Color.fromCssColorString('#16f2d4').withAlpha(0.7),
            arcType: Cesium.ArcType.NONE,
            clampToGround: false,
          },
        });

        // Subsurface frame at depth
        const DEPTH = -3200;
        viewer.entities.add({
          id: 'aoi-frame-deep',
          polyline: {
            positions: Cesium.Cartesian3.fromDegreesArrayHeights([
              west, south, DEPTH,
              east, south, DEPTH,
              east, north, DEPTH,
              west, north, DEPTH,
              west, south, DEPTH,
            ]),
            width: 1.5,
            material: Cesium.Color.fromCssColorString('#16f2d4').withAlpha(0.3),
            arcType: Cesium.ArcType.NONE,
            clampToGround: false,
          },
        });

        // Vertical risers at the four corners
        [[west, south], [east, south], [east, north], [west, north]].forEach(([lon, lat], i) => {
          viewer.entities.add({
            id: `aoi-riser-${i}`,
            polyline: {
              positions: Cesium.Cartesian3.fromDegreesArrayHeights([
                lon, lat, 5,
                lon, lat, DEPTH,
              ]),
              width: 1.5,
              material: Cesium.Color.fromCssColorString('#16f2d4').withAlpha(0.3),
              arcType: Cesium.ArcType.NONE,
              clampToGround: false,
            },
          });
        });
      } catch (e) {
        console.warn('[well3d] cutaway frame failed:', e);
      }

      // Camera controller — tune zoom and inertia so the user can navigate
      // the small AOI without runaway zoom-ins. Cesium's defaults treat each
      // wheel tick as a big fraction of the camera's distance to surface,
      // which gets exponential close to the ground.
      const cam = viewer.scene.screenSpaceCameraController;
      cam.enableCollisionDetection = false;          // allow subsurface
      cam.minimumZoomDistance      = -5000;          // ~5km below surface max
      cam.maximumZoomDistance      = 100000;         // ~100km above surface max
      cam.zoomFactor               = 3.0;            // default 5.0 — less aggressive
      cam.wheelZoomAmount          = 0.4;            // default ~1.0 — smaller ticks
      cam.inertiaZoom              = 0.4;            // default 0.8 — settle faster
      cam.inertiaTranslate         = 0.7;
      cam.inertiaSpin              = 0.7;

      viewer.camera.setView({ destination: coverageRect });

      const camListener = viewer.scene.postRender.addEventListener(() => {
        const c = viewer.camera;
        const carto = viewer.scene.globe.ellipsoid.cartesianToCartographic(c.position);
        setCamera({
          h:     carto.height,
          pitch: Cesium.Math.toDegrees(c.pitch),
          alpha: viewer.scene.globe.translucency.frontFaceAlpha,
        });
      });

      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((click) => {
        const picked = viewer.scene.pick(click.position);
        const id = picked?.id?.id;
        if (id?.startsWith?.('well:')) setSelected(id.slice(5));
        else                            setSelected(null);
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      viewerRef.current = viewer;
      viewer._handler = handler;
      viewer._camListener = camListener;
      } catch (err) {
        console.error('[well3d] viewer init failed:', err);
        setError(`Viewer init failed: ${err.message}`);
      }
    })();

    return () => {
      cancelled = true;
      sessionRef.current?.stop();
      viewerRef.current?._camListener?.();
      viewerRef.current?._handler?.destroy();
      viewerRef.current?.destroy();
      viewerRef.current = null;
      sessionRef.current = null;
    };
  }, [projectAoiId, activeOperatingAoiId, project?.id, datasetKey,
      bbox?.west, bbox?.south, bbox?.east, bbox?.north]);

  /* ---- Fetch wells: project AOI bbox + operating AOI as the entitlement filter ---- */
  useEffect(() => {
    if (!bbox || !activeOperatingAoiId) return;
    let cancelled = false;
    setLoading(true); setError(null); setWells([]); setSurfaceWells([]);

    (async () => {
      try {
        setLoadMsg('Loading wells in viewport…');
        const wellsQs = new URLSearchParams({
          bbox:   `${bbox.west},${bbox.south},${bbox.east},${bbox.north}`,
          limit:  '200',
          aoi_id: activeOperatingAoiId,  // wells/rrc-near takes operating AOI uuid
        });
        const r = await fetchWithAuth(`/api/v1/wells/rrc-near?${wellsQs}`);
        if (!r.ok) throw new Error(`wells/rrc-near ${r.status}`);
        const { wells: near } = await r.json();
        if (cancelled) return;
        setSurfaceWells(near);

        const withApi = near.filter(w => w.matched_api);
        setLoadMsg(`Loading directional data (${withApi.length} wells)…`);

        const directionals = await fetchInPool(withApi, 8, async (w) => {
          const dr = await fetchWithAuth(`/api/v1/well-directionals/${w.matched_api}`);
          if (!dr.ok) throw new Error(`directional ${dr.status}`);
          return await dr.json();
        });
        if (cancelled) return;

        const valid = directionals.filter(d => d && !d.__error && d.tvdFt);
        setWells(valid);
        setLoading(false);
        setLoadMsg('');
      } catch (e) {
        if (!cancelled) { setError(String(e.message || e)); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [bbox?.west, bbox?.south, bbox?.east, bbox?.north, activeOperatingAoiId]);

  /* ---- Render surface pads ---- */
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !surfaceWells.length) return;

    viewer.entities.values
      .filter(e => e.id?.startsWith?.('pad:'))
      .forEach(e => viewer.entities.remove(e));

    surfaceWells.forEach(w => {
      const padKey = w.matched_api || w.id;
      viewer.entities.add({
        id: `pad:${padKey}`,
        position: Cesium.Cartesian3.fromDegrees(w.lng, w.lat, 2),
        point: {
          pixelSize: 5,
          color: Cesium.Color.fromCssColorString('#888780'),
          outlineColor: Cesium.Color.WHITE.withAlpha(0.6),
          outlineWidth: 1,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        },
      });
    });
  }, [surfaceWells]);

  /* ---- Render wellbores ---- */
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !wells.length) return;

    viewer.entities.values
      .filter(e => e.id?.startsWith?.('well:'))
      .forEach(e => viewer.entities.remove(e));

    const paths = wells.map(wellToCesiumPath).filter(Boolean);
    paths.forEach(({ positions, color, meta }) => {
      try {
        viewer.entities.add({
          id: `well:${meta.api}`,
          name: meta.api,
          polyline: {
            positions,
            width: 4,
            material: color.withAlpha(0.95),
            arcType: Cesium.ArcType.NONE,
            clampToGround: false,
          },
        });
        const padEntity = viewer.entities.getById(`pad:${meta.api}`);
        if (padEntity?.point) padEntity.point.color = color;
      } catch (err) {
        console.warn(`[well3d] skipped well ${meta.api}:`, err.message);
      }
    });
  }, [wells]);

  /* ---- Imperative handle for the panel ---- */
  useImperativeHandle(ref, () => ({
    playFlythrough() {
      const viewer = viewerRef.current;
      if (!viewer || !bbox) return;
      const centerLon = (bbox.west + bbox.east) / 2;
      const centerLat = (bbox.south + bbox.north) / 2;
      const latOffsetForFraming = (h, pitchDeg) =>
        (h / Math.tan(Math.abs(pitchDeg) * Math.PI / 180)) / 111_000;

      const b1H = 7000, b1P = -45;
      viewer.scene.globe.translucency.frontFaceAlpha = 1.0;
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat - latOffsetForFraming(b1H, b1P), b1H),
        orientation: { heading: 0, pitch: Cesium.Math.toRadians(b1P), roll: 0 },
        duration: 2.5,
        complete: () => {
          const t0 = performance.now();
          const fade = () => {
            const t = Math.min(1, (performance.now() - t0) / 2500);
            viewer.scene.globe.translucency.frontFaceAlpha = 1.0 - t * 0.92;
            if (t < 1) requestAnimationFrame(fade);
          };
          fade();
          const b2H = -3200, b2P = +35;
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat - latOffsetForFraming(Math.abs(b2H), b2P), b2H),
            orientation: { heading: 0, pitch: Cesium.Math.toRadians(b2P), roll: 0 },
            duration: 4.0,
            complete: () => {
              const t0b = performance.now();
              const fadeUp = () => {
                const t = Math.min(1, (performance.now() - t0b) / 3000);
                viewer.scene.globe.translucency.frontFaceAlpha = 0.08 + t * 0.52;
                if (t < 1) requestAnimationFrame(fadeUp);
              };
              fadeUp();
              const b3H = 6500, b3P = -40;
              viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat - latOffsetForFraming(b3H, b3P), b3H),
                orientation: { heading: Cesium.Math.toRadians(15), pitch: Cesium.Math.toRadians(b3P), roll: 0 },
                duration: 4.0,
              });
            },
          });
        },
      });
    },
    snapSubsurface() {
      const viewer = viewerRef.current;
      if (!viewer || !bbox) return;
      const centerLon = (bbox.west + bbox.east) / 2;
      const centerLat = (bbox.south + bbox.north) / 2;
      viewer.scene.globe.translucency.frontFaceAlpha = 0.08;
      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat - 0.015, -3200),
        orientation: { heading: 0, pitch: Cesium.Math.toRadians(+35), roll: 0 },
      });
    },
    toggleSurface() {
      const viewer = viewerRef.current;
      if (!viewer) return;
      const cur = viewer.scene.globe.translucency.frontFaceAlpha;
      viewer.scene.globe.translucency.frontFaceAlpha = cur > 0.5 ? 0.3 : 1.0;
    },
    flyToWell(api) {
      const viewer = viewerRef.current;
      const w = surfaceWells.find(sw => sw.matched_api === api);
      if (!viewer || !w) return;
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(w.lng, w.lat - 0.005, 1500),
        orientation: { heading: 0, pitch: Cesium.Math.toRadians(-45), roll: 0 },
        duration: 1.5,
      });
    },
  }), [bbox, surfaceWells]);

  return <div ref={containerRef} className="absolute inset-0" />;
});

export default Well3DSurface;
